import { useState, useEffect, useCallback } from 'react';
import { error } from '../../../../../utils/logger';
import { runPipelineBatch, getSkipStages, refreshStoredConfig, loadGovernanceConfig, clearShadowFindings, buildShadowImpactReport, getGateThresholds, type PipelineBatchResult, type ShadowImpactReport } from '../../../../../governance';
import { ALL_QUESTIONS_CORE } from '../../../../../content';

/**
 * Gate-drempels, rechtstreeks uit de bron.
 *
 * Hier stonden drie waarden overgetypt uit `DEFAULT_GATE_THRESHOLDS`, met een
 * toelichting die zelf toegaf dat ze niet gesynchroniseerd waren. Dat is het
 * patroon dat in dit project al twee keer een bug heeft opgeleverd: twee
 * plaatsen met dezelfde waarde, waarvan er één wordt bijgewerkt. En het faalt
 * op de slechtst mogelijke manier — de adminpagina toont dan met gezag een
 * drempel waarop de gate niet meer beoordeelt.
 *
 * Het argument dat de drempels "gate-only knoppen zonder eigen weergave" zijn,
 * klopte niet: dit ís die weergave.
 */
// Bewust een functie en geen module-constante: `getGateThresholds()` leest de
// runtime-policy, en die is bij het importeren van dit bestand nog niet
// geladen. Een constante hier zou de waarden van vóór `refreshStoredConfig()`
// vastleggen — precies de drift die we wilden wegnemen, in een nieuwe vorm.
const gateDefaults = () => getGateThresholds();
export interface PipelineRunState {
  isRunning: boolean;
  lastRun: string | null;
  runDuration: string | null;
  batchResult: PipelineBatchResult | null;
  /**
   * FG4 §Fase B — Shadow-mode impact report computed alongside each pipeline
   * run. Non-null after the first run completes; counts every "would-block"
   * finding emitted by modules/types configured as enforcement mode 'shadow'.
   * `null` until the first run finishes (or when enforcement is fully 'off'
   * and no module ramps to shadow yet).
   */
  shadowImpactReport: ShadowImpactReport | null;
}
export interface PipelineRunController extends PipelineRunState {
  revalidate: () => void;
}

/**
 * usePipelineRun — owns the pipeline execution lifecycle.
 *
 * Single responsibility:
 *  - Read stored governance config (skip-stages, pedagogy toggle, quality gates,
 *    FG4 enforcement modes + grace ledger)
 *  - Clear the shadow findings store BEFORE each run so the impact report is
 *    run-scoped (not a cumulative log)
 *  - Run `runPipelineBatch` over ALL_QUESTIONS_CORE with `enforcementConfig`
 *  - Compute `buildShadowImpactReport()` AFTER the run and surface it alongside
 *    the batch result
 *  - Track timing (last run timestamp, duration) for the admin header
 *
 * Auto-runs on mount. Exposes `revalidate` for the manual "Opnieuw valideren"
 * button.
 *
 * Extracted from useGovernanceData (R1) so the run lifecycle is independent
 * of the derivation/view-model logic. Useful for future use cases like
 * comparing two runs side-by-side (snapshot-infra route 1).
 */
export function usePipelineRun(): PipelineRunController {
  const [isRunning, setIsRunning] = useState(true);
  const [lastRun, setLastRun] = useState<string | null>(null);
  const [runDuration, setRunDuration] = useState<string | null>(null);
  const [batchResult, setBatchResult] = useState<PipelineBatchResult | null>(null);
  const [shadowImpactReport, setShadowImpactReport] = useState<ShadowImpactReport | null>(null);
  const revalidate = useCallback(() => {
    setIsRunning(true);
    const startTime = Date.now();
    const now = new Date();
    const timeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

    // setTimeout lets React paint the loading state before the heavy run
    setTimeout(async () => {
      try {
        await refreshStoredConfig();
        const skipStages = await getSkipStages();
        const govConfig = await loadGovernanceConfig();
        const skipPedagogy = govConfig.pipelineStages?.pedagogyEnabled === false;
        const defaults = gateDefaults();
        const thresholds = govConfig.qualityGates ? {
          productionMinScore: govConfig.qualityGates.minQualityScore ?? defaults.productionMinScore,
          assessmentMinScore: Math.max(govConfig.qualityGates.minQualityScore != null ? govConfig.qualityGates.minQualityScore + 12 : defaults.assessmentMinScore, defaults.assessmentMinScore),
          reviewCoachSeverityThreshold: defaults.reviewCoachSeverityThreshold,
          assessmentMinQuestionsPerFamily: defaults.assessmentMinQuestionsPerFamily
        } : undefined;

        // FG4 §Fase B — clear last run's shadow findings so the impact report
        // reflects ONLY this run. Without this the store accumulates across
        // runs and the "would block X questions" number drifts.
        clearShadowFindings();
        const result = runPipelineBatch(ALL_QUESTIONS_CORE, {
          ...(skipStages.length > 0 ? {
            skipStages
          } : {}),
          skipPedagogy,
          ...(thresholds ? {
            thresholds
          } : {}),
          // FG4 §Fase B — opt-in shadow/warn/enforce ramp.
          //
          // CORRECTIE: hier stond dat een niet-geconfigureerde scope naar 'off'
          // (legacy permissive) valt. Dat is niet wat de code doet —
          // `resolvePedagogicalMode`/`resolveSchemaMode` vallen terug op
          // 'enforce'. Het gedrag is dus fail-safe, en deze toelichting
          // beschreef het tegenovergestelde. Iemand die erop vertrouwde, zou
          // een echte 'off'-fallback als "zoals bedoeld" hebben ingebouwd.
          enforcementConfig: govConfig
        });

        // FG4 §Fase B — snapshot the shadow findings into a report. Computed
        // from the module-level store that recordShadowFinding writes to.
        const shadowReport = buildShadowImpactReport();
        const duration = ((Date.now() - startTime) / 1000).toFixed(1);
        setBatchResult(result);
        setShadowImpactReport(shadowReport);
        setLastRun(`${timeStr} (${duration}s)`);
        setRunDuration(`${duration}s`);
      } catch (err) {
        error('Validation failed:', err);
      } finally {
        setIsRunning(false);
      }
    }, 50);
  }, []);
  useEffect(() => {
    revalidate();
  }, [revalidate]);
  return {
    isRunning,
    lastRun,
    runDuration,
    batchResult,
    shadowImpactReport,
    revalidate
  };
}